import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ShiftClosure } from '../models/shift-closure.model';
import { environment } from '../../../environments/environment';
import { CreateUpdateRecordDto } from '../dtos/update-record.dto';
import { UpdateRecord } from '../models/update-record.model';
import { AuthService } from './auth.service';
import { StateSearchDTO } from '../models/search-filter.model';

@Injectable({
  providedIn: 'root',
})
export class ShiftClosureService {
  private apiUrl = environment.apiUrl + environment.shiftClosure;

  constructor(private http: HttpClient, private auth: AuthService) {}

  /**
   * Crea un nuevo cierre de turno.
   * @param updateRecordIds Lista de IDs de registros de actualización
   * @returns Observable con los datos del cierre de turno creado
   */
  createShiftClosure(updateRecordIds: number[]): Observable<ShiftClosure> {
    return this.http.post<ShiftClosure>(`${this.apiUrl}`, updateRecordIds);
  }
  
  createShiftClosureComplete(payload: CreateUpdateRecordDto, closeUpdateRecords: boolean, shiftClosureId?: number, overrideUserId?: number, isHistorical?: boolean, overrideClosureMode?: string): Observable<any> {
    const currentUserId = this.auth.getUserId();
    const userIdToUse = overrideUserId !== undefined ? overrideUserId : currentUserId;
    const body: any = { ...payload };
    if (userIdToUse != null) body.userId = userIdToUse;
    if (shiftClosureId != null) body.shiftClosureId = shiftClosureId; // permitir pasar turno explícito

    // Determinar closureMode: usar override si se proporciona, sino basarse en rol
    let closureMode = overrideClosureMode || 'jefeturno';
    if (!overrideClosureMode) {
      if (this.auth.hasRole('Novedades_Operador_DCS')) {
        closureMode = 'dcs';
      } else if (this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto')) {
        closureMode = 'ferrico';
      }
    }

    // Agregar parámetro isHistorical si se proporciona
    let url = `${this.apiUrl}/create-and-close?closeUpdateRecords=${closeUpdateRecords}&closureMode=${closureMode}`;
    if (isHistorical === true) {
      url += '&isHistorical=true';
    }

    return this.http.post<any>(url, body);
  }

  startShift(payload: { shiftClosureType: string, time?: string }, overrideUserId?: number): Observable<any> {
    const userId = overrideUserId !== undefined ? overrideUserId : this.auth.getUserId();
    const body: any = { ...payload };
    if (userId != null) body.userId = userId;
    return this.http.post(`${this.apiUrl}/start`, body);
  }

  /**
   * Obtiene todos los cierres de turno registrados.
   * @returns Observable con la lista de cierres de turnos
   */
  getAllShiftClosures(): Observable<ShiftClosure[]> {
    return this.http.get<ShiftClosure[]>(`${this.apiUrl}`);
  }

  /**
   * Obtiene un cierre de turno por su ID.
   * @param id ID del cierre de turno
   * @returns Observable con los detalles del cierre de turno
   */
  getShiftClosureById(id: number): Observable<ShiftClosure> {
    return this.http.get<ShiftClosure>(`${this.apiUrl}/${id}`);
  }

  /** Nuevo: obtener turno abierto por tipo para el usuario actual */
  getOpenByTypeForCurrentUser(type: string): Observable<number> {
    const userId = this.auth.getUserId();
    return this.http.get<number>(`${this.apiUrl}/open/${encodeURIComponent(type)}/user/${userId}`);
  }

  /** Nuevo: obtener turno diario abierto (global, sin user scope) */
  getOpenDailyShift(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/open/turno_diario`);
  }

  

  /** Nuevo: obtener cierres de turno por usuario */
  getShiftClosuresByUser(userId?: number): Observable<ShiftClosure[]> {
    const id = userId ?? this.auth.getUserId();
    return this.http.get<ShiftClosure[]>(`${this.apiUrl}/user/${id}`);
  }

  getShiftClosurePdf(shiftClosureId: number, isPreview: boolean = false): Observable<Blob> {
    const viewMode = this.auth.hasRole('Novedades_Operador_DCS')
      ? 'dcs'
      : (this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto'))
        ? 'ferrico'
        : 'jefeturno';
    const base = `${this.apiUrl}/pdf/${shiftClosureId}`;
    let url = base;
    if (isPreview) {
      url = `${base}?preview=true&viewMode=${viewMode}`;
    } else {
      url = `${base}?viewMode=${viewMode}`;
    }
    // Nuevo: adjuntar userId solo cuando el viewMode sea 'ferrico'
    if (viewMode === 'ferrico') {
      const userId = this.auth.getUserId();
      if (userId != null) {
        url += `&userId=${encodeURIComponent(String(userId))}`;
      }
    }
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Obtiene el ID de un turno abierto para el tipo especificado, si existe.
   * @param type p.e. 'informe_turno' o 'turno_diario'
   * @returns Observable<number> con el shiftClosureId, o error 404 si no existe
   */
  getOpenShiftByType(type: string): Observable<number> {
    const userId = this.auth.getUserId();
    return this.http.get<number>(`${this.apiUrl}/open/${encodeURIComponent(type)}/user/${userId}`);
  }

  

  /**
   * Igual a getOpenShiftByType, pero permite especificar un userId explícito.
   * Útil para casos especiales (p.ej., daily-shift requiere userId=1).
   */
  getOpenShiftByTypeForUser(type: string, userId: number): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/open/${encodeURIComponent(type)}/user/${userId}`);
  }

  getPreviousShiftIdByType(type: string): Observable<number> {
    const userId = this.auth.getUserId();
    //return this.http.get<number>(`${this.apiUrl}/previous/${encodeURIComponent(type)}/user/${userId}`);
    return this.http.get<number>(`${this.apiUrl}/previous/${encodeURIComponent(type)}`);
  }

  getShiftClosurePdfPreview(payload: any): Observable<Blob> {
    const viewMode = this.auth.hasRole('Novedades_Operador_DCS')
      ? 'dcs'
      : (this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto'))
        ? 'ferrico'
        : 'jefeturno';
    // Construir URL y adjuntar userId solo cuando el viewMode sea 'ferrico'
    let url = `${this.apiUrl}/pdf-preview?viewMode=${viewMode}`;
    if (viewMode === 'ferrico') {
      const userId = this.auth.getUserId();
      if (userId != null) {
        url += `&userId=${encodeURIComponent(String(userId))}`;
      }
    }
    return this.http.post(url, payload, { responseType: 'blob' });
  }

  /**
   * Crea un nuevo registro de actualización y lo asocia a un cierre de turno existente.
   * Se realiza en una única transacción para garantizar la consistencia (si falla alguna parte, se revierte todo).
   * 
   * @param payload Los datos necesarios para crear el registro de actualización.
   * @param shiftClosureId El ID del cierre de turno al cual se asociará el registro.
   * @returns Observable con el UpdateRecord creado.
   */
  createAndAssociateUpdateRecord(payload: CreateUpdateRecordDto, shiftClosureId: number): Observable<UpdateRecord> {
    const userId = this.auth.getUserId();
    const body: any = { ...payload };
    if (userId != null) body.userId = userId;
    return this.http.post<UpdateRecord>(`${this.apiUrl}/update-record/create-and-associate?shiftClosureId=${shiftClosureId}`, body);
  }

  /**
   * Actualiza el estado de disponibilidad del informe para un cierre de turno.
   * 
   * @param id ID del cierre de turno
   * @param isAvailable Nuevo estado del informe (true/false)
   * @returns Observable<void>
   */
  updateReportAvailability(id: number, isAvailable: boolean): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/report-availability`, isAvailable);
  }

  /** Nuevo: listado de cierres de turno por usuario */
  getByUser(userId?: number): Observable<ShiftClosure[]> {
    const id = userId ?? this.auth.getUserId();
    return this.http.get<ShiftClosure[]>(`${this.apiUrl}/user/${id}`);
  }

  closeShiftClosure(shiftClosureId: any): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/close?shiftClosureId=${shiftClosureId}`, {});
  }

  

  
  
}
